import { lineDrawing, type Drawing } from './picture-tiles'
import { clear, el, money, parseMoney } from './ui'
import { currencySymbol } from './round-config'
import { extrasOf, extrasTotal, type Extra, type ExtraKind } from './extras'
import { currentTrade } from './trade'
import { getMe } from './me'

// Extras? on Job done (Liam, 27 Oct 2026: "next to part done there could be extras", then "I like the bolder tiles ...
// also extra should have a ?"; https://claude.ai/artifact/W1dK9QZUNja18BSr9d7XMF, checked by Astra). A quiet "Extras?"
// to the right of "Part done?"; tapped, picture tiles open in place: Insides, Gutters, Conservatory roof and Something
// else. A tapped tile turns lemon with a tick and Save shows the new total. What was booked on the visit starts ticked
// ("Booked"), so nothing is counted twice. Insides are the visit's inside_pence (0070), the rest its extras_json (0087);
// amount_pence stays the whole price, the job's own price kept as it was. The owner's phone only for now: a helper's
// extras wait for the owner to price them (the next step of the drawings, E6 and E7).

export interface DoneExtrasChange { amount_pence: number; inside_pence: number | null; extras_json: Extra[] | null }
export interface DoneExtras {
  link: HTMLButtonElement
  panel: HTMLElement
  count: () => number
  changed: () => boolean
  total: () => number | null
  problem: () => string | null
  value: () => DoneExtrasChange | null
  setLocked: (why: string | null) => void
}

interface VisitPrice { amount_pence?: number | null; inside_pence?: number | null; extras_json?: string | null }
type Row = { kind: ExtraKind; name: string; count: number; priceText: string; booked: boolean }

const DRAW: Record<'insides' | ExtraKind, Drawing> = { insides: 'door', gutters: 'note', conservatory: 'house', other: 'pencil' }
const DEFAULT_EACH: Partial<Record<ExtraKind, number>> = { conservatory: 1500 }

// The price last typed for an extra on this phone and round, offered next time ("used next time too", the drawings'
// E3P). Only a starting figure: the owner can change it on any visit.
const priceKey = (kind: ExtraKind): string => `roundly:extra-price:${getMe()?.businessId ?? 0}:${kind}`
export function rememberedEach(kind: ExtraKind): number | null {
  try { const raw = localStorage.getItem(priceKey(kind)); const v = raw === null || !raw.trim() ? NaN : Number(raw); return Number.isInteger(v) && v >= 0 && v <= 1000000 ? v : null } catch { return null }
}
export function remember(kind: ExtraKind, pence: number): void {
  try { localStorage.setItem(priceKey(kind), String(pence)) } catch { /* private window: nothing kept */ }
}
const toText = (p: number | null): string => (p === null ? '' : (p / 100).toFixed(2))
const textPence = (t: string): number | null => { try { return parseMoney(t) } catch { return null } }

function extraTile(key: 'insides' | ExtraKind, title: string, on: boolean, booked: boolean, sub: string): HTMLButtonElement {
  return el('button', { type: 'button', class: `wd-extra-tile${on ? ' is-on' : ''}`, 'aria-pressed': String(on) },
    el('span', { class: 'wd-extra-top' },
      el('span', { class: 'booking-extras-ring', 'aria-hidden': 'true' }, lineDrawing(DRAW[key], 18)),
      el('span', { class: 'wd-extra-marks' }, ...(booked ? [el('span', { class: 'wd-extra-booked' }, 'Booked')] : []), ...(on ? [lineDrawing('tick', 18)] : []))),
    el('span', { class: 'wd-extra-words' }, el('strong', {}, title), el('span', {}, sub))) as HTMLButtonElement
}

export function doneExtras(visit: VisitPrice, customerInside: number | null, onChange: () => void): DoneExtras {
  const bookedInside = typeof visit.inside_pence === 'number' && visit.inside_pence > 0 ? visit.inside_pence : 0
  const bookedExtras = extrasOf(visit.extras_json)
  const whole = typeof visit.amount_pence === 'number' ? visit.amount_pence : null
  // The job's own price, as booked: the whole less what was booked on top of it.
  const base = whole === null ? null : whole - bookedInside - extrasTotal(bookedExtras)
  const windows = currentTrade() === 'window_cleaning'
  let insidesOn = bookedInside > 0
  let insideText = toText(bookedInside > 0 ? bookedInside : customerInside)
  const rows: Row[] = bookedExtras.map((e) => ({ kind: e.kind, name: e.name, count: e.count, priceText: toText(e.each_pence ?? null), booked: true }))
  let open = false
  let locked: string | null = null

  const link = el('button', { type: 'button', class: 'link-button wd-extras-open', 'aria-expanded': 'false' }) as HTMLButtonElement
  const panel = el('div', { class: 'wd-extras', hidden: '' })

  const insidePence = (): number | null => (insidesOn ? textPence(insideText) : null)
  const eachOf = (r: Row): number | null => textPence(r.priceText)
  const picked = (kind: ExtraKind): Row[] => rows.filter((r) => r.kind === kind)
  const count = (): number => (insidesOn ? 1 : 0) + rows.length
  const changed = (): boolean => {
    if (insidesOn !== bookedInside > 0) return true
    if (insidesOn && insidePence() !== bookedInside) return true
    const now = rows.map((r) => ({ kind: r.kind, name: r.name.trim(), count: r.count, each_pence: eachOf(r) }))
    return JSON.stringify(now) !== JSON.stringify(bookedExtras.map((e) => ({ kind: e.kind, name: e.name, count: e.count, each_pence: e.each_pence ?? null })))
  }
  const addedPence = (): number => (insidePence() ?? 0) + rows.reduce((s, r) => s + r.count * (eachOf(r) ?? 0), 0)
  const total = (): number | null => (base === null ? null : base + addedPence())

  const problem = (): string | null => {
    if (!changed()) return null
    if (base === null) return 'This job has no price yet. Put its price on first (tap the price on the job), then add extras.'
    if (insidesOn && insidePence() === null) return 'Put in the insides price, or tap Insides to take them off.'
    for (const r of rows) {
      if (r.kind === 'other' && !r.name.trim()) return 'Say what the something else was, for example sills scrubbed.'
      if (eachOf(r) === null) return `Put in a price for ${r.kind === 'other' ? r.name.trim() || 'the something else' : r.name.toLowerCase()}.`
    }
    return null
  }
  const value = (): DoneExtrasChange | null => {
    if (!changed() || problem() !== null || base === null) return null
    for (const r of rows) if (r.kind !== 'other' && !r.booked) remember(r.kind, eachOf(r) as number)
    const list: Extra[] = rows.map((r) => ({ kind: r.kind, name: r.kind === 'other' ? r.name.trim() : r.name, count: r.count, each_pence: eachOf(r) as number }))
    return { amount_pence: total() as number, inside_pence: insidesOn ? insidePence() : null, extras_json: list.length ? list : null }
  }

  const words = (): string => (count() ? `Extras? · ${count()}` : 'Extras?')
  const drawLink = (): void => {
    link.replaceChildren(words(), lineDrawing(open ? 'chevronUp' : 'chevronDown', 16))
    link.setAttribute('aria-expanded', String(open))
    link.classList.toggle('is-set', count() > 0 || open)
    link.disabled = locked !== null
  }

  const tile = extraTile
  const priceBox = (label: string, text: string, set: (t: string) => void, each = false): HTMLElement => {
    const input = el('input', { type: 'text', inputmode: 'decimal', class: 'wd-extra-price', 'aria-label': label, placeholder: 'Price' }) as HTMLInputElement
    input.value = text
    input.addEventListener('input', () => { set(input.value); refresh() })
    return el('label', { class: 'wd-extra-each' }, el('span', {}, `${currencySymbol()}${each ? ' a panel' : ''}`), input)
  }

  const tilesBox = el('div', { class: 'wd-extra-tiles' })
  const rowsBox = el('div', { class: 'wd-extra-rows' })
  const sum = (r: Row): string => { const e = eachOf(r); return e === null ? 'Set a price' : `+ ${money(r.count * e)}` }

  const drawTiles = (): void => {
    clear(tilesBox)
    if (windows || bookedInside > 0) {
      const t = tile('insides', 'Insides', insidesOn, bookedInside > 0, insidesOn ? (insidePence() === null ? 'Set a price' : `+ ${money(insidePence() as number)}`) : insideText ? `+ ${money(textPence(insideText) ?? 0)}` : 'Tap to add')
      t.addEventListener('click', () => { insidesOn = !insidesOn; refresh(true) })
      tilesBox.append(t)
    }
    const kindTile = (kind: ExtraKind, title: string, sub: string): void => {
      const mine = picked(kind)
      const t = tile(kind, title, mine.length > 0, mine.some((r) => r.booked), mine.length ? sum(mine[0]) : sub)
      t.addEventListener('click', () => {
        if (mine.length) { for (const r of mine) rows.splice(rows.indexOf(r), 1) } else {
          const each = rememberedEach(kind) ?? DEFAULT_EACH[kind] ?? null
          rows.push({ kind, name: kind === 'other' ? '' : title, count: 1, priceText: toText(each), booked: false })
        }
        refresh(true)
        if (!mine.length) panel.querySelector<HTMLInputElement>(kind === 'other' ? '.wd-extra-row:last-child .wd-extra-name' : '.wd-extra-row:last-child .wd-extra-price')?.focus({ preventScroll: true })
      })
      tilesBox.append(t)
    }
    kindTile('gutters', 'Gutters', 'Tap to add')
    kindTile('conservatory', 'Conservatory roof', `${money(rememberedEach('conservatory') ?? 1500)} a panel`)
    kindTile('other', 'Something else', 'Type it')
  }

  const drawRows = (): void => {
    clear(rowsBox)
    if (insidesOn) rowsBox.append(el('div', { class: 'wd-extra-row' }, el('strong', { class: 'wd-extra-label' }, 'Insides'),
      priceBox('Insides price', insideText, (t) => { insideText = t })))
    for (const r of rows) {
      const bits: HTMLElement[] = []
      if (r.kind === 'other') {
        const name = el('input', { type: 'text', maxlength: '60', class: 'wd-extra-name', 'aria-label': 'What it was', placeholder: 'What it was' }) as HTMLInputElement
        name.value = r.name
        name.addEventListener('input', () => { r.name = name.value; refresh() })
        bits.push(name)
      } else bits.push(el('strong', { class: 'wd-extra-label' }, r.name))
      if (r.kind === 'conservatory') {
        const n = el('span', { class: 'booking-extra-count' }, String(r.count))
        const minus = el('button', { type: 'button', class: 'booking-extra-step', 'aria-label': 'One panel fewer' }, '−')
        const plus = el('button', { type: 'button', class: 'booking-extra-step', 'aria-label': 'One panel more' }, '+')
        minus.addEventListener('click', () => { if (r.count > 1) { r.count -= 1; refresh(true) } })
        plus.addEventListener('click', () => { if (r.count < 99) { r.count += 1; refresh(true) } })
        bits.push(el('span', { class: 'booking-extra-counter', 'aria-label': 'Panels' }, minus, n, plus))
      }
      bits.push(priceBox(`${r.kind === 'other' ? 'Something else' : r.name} price${r.kind === 'conservatory' ? ' a panel' : ''}`, r.priceText, (t) => { r.priceText = t }, r.kind === 'conservatory'))
      rowsBox.append(el('div', { class: `wd-extra-row wd-extra-row-${r.kind}` }, ...bits))
    }
    if (picked('other').length) {
      const more = el('button', { type: 'button', class: 'link-button wd-extra-more' }, '+ Another')
      more.addEventListener('click', () => { rows.push({ kind: 'other', name: '', count: 1, priceText: '', booked: false }); refresh(true); panel.querySelector<HTMLInputElement>('.wd-extra-row-other:last-of-type .wd-extra-name')?.focus({ preventScroll: true }) })
      rowsBox.append(more)
    }
  }

  // Redraw: the tiles and rows on a tap (full), only the link, sums and Save while typing (a box keeps its cursor).
  function refresh(full = false): void {
    if (full) { drawTiles(); drawRows() } else {
      // Typing changes sums only: redraw the tiles (no inputs in them), keep the rows and their cursors.
      drawTiles()
    }
    drawLink()
    onChange()
  }

  link.addEventListener('click', () => { if (locked) return; open = !open; panel.hidden = !open; refresh(true) })
  drawLink(); drawTiles(); drawRows()
  panel.append(tilesBox, rowsBox)
  return {
    link, panel, count, changed, total, problem, value,
    setLocked: (why) => { locked = why; if (why && open) { open = false; panel.hidden = true }; drawLink() },
  }
}

// A helper's Extras? (0091; Liam, 27 Oct 2026: "its not just sendi its anyone assigned as a worker"; the drawings' E6):
// the same tiles with no prices. What they tick is sent with Done as extras_asked and waits in the owner's Worker notes
// to be charged or not; the visit's price never changes from a helper's phone.
export interface AskExtras {
  link: HTMLButtonElement
  panel: HTMLElement
  count: () => number
  problem: () => string | null
  asked: () => AskedExtra[] | null
  setLocked: (why: string | null) => void
}
export type AskedKind = 'insides' | ExtraKind
export interface AskedExtra { kind: AskedKind; name: string; count: number }

export function askExtras(visit: { extras_json?: string | null }, onChange: () => void): AskExtras {
  const booked = extrasOf(visit.extras_json)
  const rows: { kind: AskedKind; name: string; count: number }[] = []
  let open = false
  let locked: string | null = null
  const link = el('button', { type: 'button', class: 'link-button wd-extras-open', 'aria-expanded': 'false' }) as HTMLButtonElement
  const panel = el('div', { class: 'wd-extras', hidden: '' })
  const tilesBox = el('div', { class: 'wd-extra-tiles' })
  const rowsBox = el('div', { class: 'wd-extra-rows' })
  const line = el('p', { class: 'wd-extra-ask-line', hidden: '' }, 'You added extras. The owner sees what you did and sets the price.')
  const count = (): number => rows.length
  const drawLink = (): void => {
    link.replaceChildren(count() ? `Extras? · ${count()}` : 'Extras?', lineDrawing(open ? 'chevronUp' : 'chevronDown', 16))
    link.setAttribute('aria-expanded', String(open))
    link.classList.toggle('is-set', count() > 0 || open)
    link.disabled = locked !== null
  }
  const drawTiles = (): void => {
    clear(tilesBox)
    const add = (kind: AskedKind, title: string, sub: string): void => {
      const mine = rows.filter((r) => r.kind === kind)
      const was = kind !== 'insides' && booked.some((e) => e.kind === kind)
      const t = extraTile(kind, title, mine.length > 0, was, mine.length && kind === 'conservatory' ? `${mine[0].count} panel${mine[0].count === 1 ? '' : 's'}` : sub)
      t.addEventListener('click', () => {
        if (mine.length) { for (const r of mine) rows.splice(rows.indexOf(r), 1) } else rows.push({ kind, name: kind === 'other' ? '' : title, count: 1 })
        refresh(true)
        if (!mine.length && kind === 'other') panel.querySelector<HTMLInputElement>('.wd-extra-row:last-of-type .wd-extra-name')?.focus({ preventScroll: true })
      })
      tilesBox.append(t)
    }
    if (currentTrade() === 'window_cleaning') add('insides', 'Insides', 'Tap if you did them')
    add('gutters', 'Gutters', 'Tap if you did them')
    add('conservatory', 'Conservatory roof', 'Panels')
    add('other', 'Something else', 'Type it')
  }
  const drawRows = (): void => {
    clear(rowsBox)
    for (const r of rows) {
      if (r.kind === 'conservatory') {
        const n = el('span', { class: 'booking-extra-count' }, String(r.count))
        const minus = el('button', { type: 'button', class: 'booking-extra-step', 'aria-label': 'One panel fewer' }, '−')
        const plus = el('button', { type: 'button', class: 'booking-extra-step', 'aria-label': 'One panel more' }, '+')
        minus.addEventListener('click', () => { if (r.count > 1) { r.count -= 1; refresh(true) } })
        plus.addEventListener('click', () => { if (r.count < 99) { r.count += 1; refresh(true) } })
        rowsBox.append(el('div', { class: 'wd-extra-row' }, el('strong', { class: 'wd-extra-label' }, 'Conservatory roof panels'), el('span', { class: 'booking-extra-counter' }, minus, n, plus)))
      } else if (r.kind === 'other') {
        const name = el('input', { type: 'text', maxlength: '60', class: 'wd-extra-name', 'aria-label': 'What it was', placeholder: 'What it was' }) as HTMLInputElement
        name.value = r.name
        name.addEventListener('input', () => { r.name = name.value; onChange() })
        rowsBox.append(el('div', { class: 'wd-extra-row wd-extra-row-other' }, name))
      }
    }
    if (rows.some((r) => r.kind === 'other')) {
      const more = el('button', { type: 'button', class: 'link-button wd-extra-more' }, '+ Another')
      more.addEventListener('click', () => { rows.push({ kind: 'other', name: '', count: 1 }); refresh(true) })
      rowsBox.append(more)
    }
    line.hidden = !rows.length
  }
  function refresh(full = false): void { if (full) { drawTiles(); drawRows() } drawLink(); onChange() }
  link.addEventListener('click', () => { if (locked) return; open = !open; panel.hidden = !open; refresh(true) })
  drawLink(); drawTiles(); drawRows()
  panel.append(tilesBox, rowsBox, line)
  return {
    link, panel, count,
    problem: () => (rows.some((r) => r.kind === 'other' && !r.name.trim()) ? 'Say what the something else was, for example sills scrubbed.' : null),
    asked: () => (rows.length ? rows.map((r) => ({ kind: r.kind, name: r.name.trim(), count: r.count })) : null),
    setLocked: (why) => { locked = why; if (why && open) { open = false; panel.hidden = true }; drawLink() },
  }
}
